/**
 * Monta o menu lateral a partir do que os modulos declaram, filtrado por
 * permissao.
 *
 * Substitui o caminho antigo, em que a estrutura vinha da arvore do /appmenu e
 * era casada com os modulos por caminho de titulos ("Resources/Settings/
 * Workgroups"). Dois problemas com aquilo: o casamento por texto quebrava ao
 * renomear um no, e o catalogo precisava ser mantido so para o menu do proprio
 * app aparecer -- catalogo vazio, menu vazio.
 *
 * Agora a estrutura e do modulo e a permissao so decide visibilidade. A arvore
 * do /appmenu continua existindo para o que ela e boa: o menu ENTRE
 * aplicacoes, que o admin desenha.
 *
 * Funcao pura: nao importa Vue, Vite nem toca em localStorage.
 */
// Import relativo, nao pelo alias '@': e o que deixa o arquivo rodar sob
// `node --test` sem bundler, como os outros puros desta pasta.
import { pode } from '../services/permissao.js';

const ICONE_PADRAO = 'folder';
const URL_VAZIA = '#';

/**
 * @param {Array}  navs     declaracoes `nav` dos modulos
 * @param {Array}  codigos  codigos do usuario, ou null quando desconhecido
 * @param {Array}  registrados  codigos que existem no ambiente, ou null
 * @param {boolean} confiavel  false quando o cache pode ser de outra
 *                             identidade -- ver `permissaoEhDesconfiavel()`
 *                             em `services/permissao.js`. Item SEM
 *                             `permissao` (ex: "Meu perfil") continua
 *                             passando mesmo assim.
 * @returns {Array} nos no formato que o LeftSidebar renderiza
 */
export function montarMenu(navs = [], codigos = null, registrados = null, confiavel = true) {
    const raiz = { filhos: new Map(), ordem: [] };

    navs
        .filter((nav) => nav?.title && nav?.route)
        .filter((nav) => !nav.dontshow)
        // A permissao e da folha. Pasta nao tem codigo proprio: ela aparece se
        // sobrou algum filho, senao sumiria levando junto um item permitido.
        .filter((nav) => pode(nav.permissao, codigos, registrados, confiavel))
        .sort(ordenar)
        .forEach((nav) => inserir(raiz, nav));

    return materializar(raiz);
}

function ordenar(a, b) {
    const sa = a.sequence ?? 0;
    const sb = b.sequence ?? 0;

    if (sa !== sb) {
        return sa - sb;
    }

    return String(a.title).localeCompare(String(b.title), 'pt-BR');
}

function inserir(raiz, nav) {
    const segmentos = String(nav.section || '')
        .split('/')
        .map((s) => s.trim())
        .filter(Boolean);

    let no = raiz;

    segmentos.forEach((segmento) => {
        if (!no.filhos.has(segmento)) {
            no.filhos.set(segmento, { titulo: segmento, filhos: new Map(), ordem: [] });
            no.ordem.push({ tipo: 'pasta', chave: segmento });
        }
        no = no.filhos.get(segmento);
    });

    no.ordem.push({ tipo: 'folha', nav });
}

function materializar(no) {
    return no.ordem
        .map((entrada) => {
            if (entrada.tipo === 'folha') {
                const { nav } = entrada;
                return {
                    title: nav.title,
                    icon: nav.icon || ICONE_PADRAO,
                    url: nav.route,
                    accordion: false,
                    menu: [],
                };
            }

            const pasta = no.filhos.get(entrada.chave);
            const filhos = materializar(pasta);

            // Pasta que ficou sem filho visivel nao entra.
            if (filhos.length === 0) {
                return null;
            }

            return {
                title: pasta.titulo,
                icon: ICONE_PADRAO,
                url: URL_VAZIA,
                accordion: true,
                menu: filhos,
            };
        })
        .filter(Boolean);
}
